/*
 * Ruler guides — precise positions while dragging (Phase 60).
 *
 *  formatGuidePosition(px, unit)   "2.375 in" / "6.03 cm" / "171.0 pt" / "228 px" — finer than the general pxToUnit readout
 *  inchFraction(px)                "2 3/8" for positions on a 1/16-inch tick (null otherwise) — how Publisher users think
 *  snapGuidePosition(raw, ctx)     the position a dragged guide lands on: page centre, page edges and other guides of the
 *                                  same orientation (margins, column / row guides, fold lines …) win when within
 *                                  GUIDE_SNAP_SCREEN_PX; otherwise the nearest ruler tick (1/8 in, 1 mm, 6 pt, 10 px).
 *                                  Alt (ctx.free) drags freely.
 */
const DPI = 96;
/** Minor ruler tick in page px — ruler markers and guides snap to it unless Alt is held (1/8 in, 1 mm, 6 pt, 10 px). */
export const rulerSnapStep = (unit) => (unit === 'cm' ? DPI / 25.4 : unit === 'px' ? 10 : unit === 'pt' ? 8 : 12);
/** Screen pixels within which a dragged guide jumps to the centre / an edge / another guide. */
export const GUIDE_SNAP_SCREEN_PX = 6;

const DIGITS = { in: 3, cm: 2, mm: 1, pt: 1, px: 0 };
export const unitDigits = (unit) => DIGITS[unit] ?? 2;

/** Position in the document unit, at the precision the unit is laid out in. */
export const formatGuidePosition = (px, unit = 'in') => {
  const v = unit === 'cm' ? (px / DPI) * 2.54 : unit === 'mm' ? (px / DPI) * 25.4 : unit === 'in' ? px / DPI : unit === 'pt' ? (px * 72) / DPI : px;
  const d = unitDigits(unit);
  const s = d ? v.toFixed(d) : String(Math.round(v));
  return `${s === '-0' || s === `-0.${'0'.repeat(d)}` ? s.slice(1) : s} ${unit}`;
};

/** "2 3/8" when the position sits on a sixteenth of an inch (whole inches read "2"); null when it does not. */
export const inchFraction = (px) => {
  const inches = px / DPI;
  const sixteenths = inches * 16;
  if (Math.abs(sixteenths - Math.round(sixteenths)) > 0.01) return null;
  const n = Math.round(sixteenths);
  const whole = Math.trunc(n / 16);
  let num = Math.abs(n % 16), den = 16;
  while (num && num % 2 === 0) { num /= 2; den /= 2; }
  const sign = n < 0 ? '-' : '';
  if (!num) return `${sign}${Math.abs(whole)}`;
  return `${sign}${Math.abs(whole) ? `${Math.abs(whole)} ` : ''}${num}/${den}`;
};

const TARGET_LABEL = { margin: 'margin', grid: 'layout guide', fold: 'fold line', panel: 'panel margin', center: 'panel centre', guide: 'guide' };

/**
 * @param raw          dragged position (page px along the axis the guide crosses)
 * @param ctx.orientation 'horizontal' | 'vertical'
 * @param ctx.guides   state.guides (positions of the same orientation are snap targets; `ctx.skipId` is the dragged one)
 * @param ctx.pageSize page height for a horizontal guide, page width for a vertical one
 * @param ctx.unit     document unit (ruler tick)
 * @param ctx.zoom     screen px per page px (tolerance is measured on screen)
 * @param ctx.free     Alt held — no snapping at all
 * @returns { position, snap: null | { kind: 'centre'|'edge'|'guide'|'tick', label } }
 */
export const snapGuidePosition = (raw, { orientation, guides = [], pageSize, unit = 'in', zoom = 1, skipId, free = false } = {}) => {
  if (!Number.isFinite(raw)) return { position: raw, snap: null };
  if (free) return { position: raw, snap: null };
  const tol = GUIDE_SNAP_SCREEN_PX / Math.max(0.05, zoom);
  const targets = [];
  if (pageSize > 0) {
    targets.push({ position: pageSize / 2, kind: 'centre', label: 'page centre' });
    targets.push({ position: 0, kind: 'edge', label: 'page edge' }, { position: pageSize, kind: 'edge', label: 'page edge' });
  }
  guides.forEach((g) => {
    if (!g || g.id === skipId || g.orientation !== orientation || !Number.isFinite(g.position)) return;
    targets.push({ position: g.position, kind: 'guide', label: TARGET_LABEL[g.kind || 'guide'] || 'guide' });
  });
  let best = null;
  targets.forEach((t) => {
    const d = Math.abs(t.position - raw);
    if (d <= tol && (!best || d < best.d)) best = { ...t, d };
  });
  if (best) return { position: best.position, snap: { kind: best.kind, label: best.label } };
  const step = rulerSnapStep(unit);
  return { position: Math.round(raw / step) * step, snap: { kind: 'tick', label: null } };
};
